import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, forwardRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MessageService } from 'base';
import { ConfigurationService } from '../../services/configuration.service';
import { OnDestroy } from '@angular/core';
import { ComponentBase } from 'common';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';
import { Integration, IntegrationNames, IntegrationTable } from '../../../DiscoveryEngine.TS/Core/Integration';
import { DefaultIntegrationSettingsChild } from '../default-integration-settings-child-interface/default-integration-settings-child-interface';


@Component({
	selector: 'ninja-one-default-settings',
	templateUrl: './ninja-one-default-settings.component.html',
	styleUrls: ['./ninja-one-default-settings.component.scss']
})
export class NinjaOneDefaultSettingsComponent extends ComponentBase implements DefaultIntegrationSettingsChild, OnInit, OnDestroy, OnChanges {
	@Input()
	public inputSettings: Integration;

	public form: UntypedFormGroup;

	public enabled: UntypedFormControl;

	public serverURL: UntypedFormControl;
	public clientID: UntypedFormControl;
	public clientSecret: UntypedFormControl;

	private clientSecretChanged: boolean;
	private clientSecretChangedSub: Subscription;

	constructor(private fb: UntypedFormBuilder,
		private configService: ConfigurationService,
		private messageService: MessageService) {

		super();
	}

	public ngOnInit(): void {
		this.buildForm();
	}

	public ngOnDestroy(): void {
		if (this.clientSecretChangedSub) {
			this.clientSecretChangedSub.unsubscribe();
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {
		this.enabled = undefined;
		this.serverURL = undefined;
		this.clientID = undefined;
		this.clientSecret = undefined;

		let enabled = true;
		let serverURL = "";
		let clientID = "";
		let clientSecret = "";

		// Load in the settings from the input settings variable, and set them to the form controls.
		if (this.inputSettings) {
			enabled = this.inputSettings.Enabled;
			serverURL = this.inputSettings.ServerURL;
			clientID = this.inputSettings.ClientID;
			clientSecret = (this.inputSettings.HasClientSecret ? this.hidePassword(this.inputSettings.ClientSecret) : "");
		}

		this.enabled = new UntypedFormControl(enabled);
		this.serverURL = new UntypedFormControl(serverURL);
		this.clientID = new UntypedFormControl(clientID);
		this.clientSecret = new UntypedFormControl(clientSecret);

		this.clientSecretChanged = false;
		this.clientSecretChangedSub = this.clientSecret.valueChanges.subscribe(() => {
			this.clientSecretChanged = true;
		});

		this.form = this.fb.group({
			enabled: this.enabled,
			serverURL: this.serverURL,
			clientID: this.clientID,
			clientSecret: this.clientSecret,
		},
		{
			validator: Validators.compose([
				this.noneOrAllSettings(),
			])
		});
	}

	public enableSave(): boolean {
		return !this.form.invalid;
	}

	public onSave(): void {
		const outputIntegrationTable = new IntegrationTable();
		const integrationSettings = this.getIntegrationSettings();
		outputIntegrationTable.Add(integrationSettings);
		this.configService.setIntegrationTable(outputIntegrationTable).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully saved NinjaOne default settings");
			},
			(error) => {
				this.messageService.displayErrorMessage("Failed to save NinjaOne default settings");
			});
	}

	public onClientSecretKeyDownHandler(event: any): void {
		if (!this.clientSecretChanged) {
			this.clientSecretChanged = true;

			this.clientSecret.setValue('');
			this.clientSecret.markAsDirty();
		}
	}

	public getIntegrationSettings(): Integration {
		const outputIntegration = new Integration(IntegrationNames.NinjaOne);
		outputIntegration.Enabled = this.enabled.value;
		outputIntegration.ServerURL = this.serverURL.value;
		outputIntegration.ClientID = this.clientID.value;
		if (this.clientSecretChanged) {
			outputIntegration.ClientSecret = StringUtils.ObfuscateString2(this.clientSecret.value);
		}
		else {
			outputIntegration.ClientSecret = this.inputSettings.ClientSecret;
		}

		return outputIntegration;
	}

	private noneOrAllSettings(): ValidatorFn {
		return () => {
			let b1 = (!!this.serverURL.value !== !!this.clientID.value);
			let b2 = (!!this.clientID.value !== !!this.clientSecret.value);
			if (b1 || b2) {
				return {
					noneOrAllSettings: { valid: false }
				};
			}

			return null;
		}
	}

}
